/**
 * PasswordStrengthMeter Component
 *
 * Displays real-time password strength and requirement checklist
 *
 * Requirements:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 *
 * Uses zxcvbn library for strength calculation (0-4 score)
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import zxcvbn from 'zxcvbn';
import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';

interface PasswordStrengthMeterProps {
  /**
   * The password to validate
   */
  password: string;

  /**
   * Callback when password meets all requirements
   */
  onValidityChange?: (isValid: boolean) => void;

  /**
   * Show/hide the component
   */
  show?: boolean;
}

interface PasswordRequirement {
  label: string;
  met: boolean;
  test: (password: string) => boolean;
}

/**
 * PasswordStrengthMeter component
 * Shows visual strength indicator and requirements checklist
 */
export function PasswordStrengthMeter({
  password,
  onValidityChange,
  show = true,
}: PasswordStrengthMeterProps) {
  const { t } = useTranslation('auth');
  const [strength, setStrength] = useState(0);

  // Define requirements with test functions
  const getRequirements = (): PasswordRequirement[] => [
    {
      label: t('passwordStrength.minLength', 'At least 8 characters'),
      met: false,
      test: (pwd) => pwd.length >= 12,
    },
    {
      label: t('passwordStrength.uppercase', 'Contains uppercase letter (A-Z)'),
      met: false,
      test: (pwd) => /[A-Z]/.test(pwd),
    },
    {
      label: t('passwordStrength.lowercase', 'Contains lowercase letter (a-z)'),
      met: false,
      test: (pwd) => /[a-z]/.test(pwd),
    },
    {
      label: t('passwordStrength.number', 'Contains number (0-9)'),
      met: false,
      test: (pwd) => /[0-9]/.test(pwd),
    },
    {
      label: t('passwordStrength.special', 'Contains special character (!@#$%^&*)'),
      met: false,
      test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
    },
  ];

  const [requirements, setRequirements] = useState<PasswordRequirement[]>(getRequirements());

  // Update requirement labels when language changes
  useEffect(() => {
    const updatedRequirements = getRequirements();
    // Preserve the 'met' status when updating labels
    setRequirements((prev) =>
      updatedRequirements.map((req, index) => ({
        ...req,
        met: prev[index]?.met || false,
      }))
    );
  }, [t]);

  useEffect(() => {
    if (!password) {
      setStrength(0);
      setRequirements((prev) =>
        prev.map((req) => ({ ...req, met: false }))
      );
      onValidityChange?.(false);
      return;
    }

    // Calculate strength using zxcvbn (0-4 score)
    const result = zxcvbn(password);
    setStrength(result.score);

    // Check requirements
    const updatedRequirements = requirements.map((req) => ({
      ...req,
      met: req.test(password),
    }));
    setRequirements(updatedRequirements);

    // Check if all requirements are met
    const allMet = updatedRequirements.every((req) => req.met);
    onValidityChange?.(allMet);
  }, [password]);

  if (!show || !password) {
    return null;
  }

  // Determine strength label and color
  const getStrengthInfo = () => {
    if (strength === 0) {
      return { label: t('passwordStrength.veryWeak', 'Very Weak'), color: 'bg-red-500', textColor: 'text-red-600' };
    }
    if (strength === 1) {
      return { label: t('passwordStrength.weak', 'Weak'), color: 'bg-red-400', textColor: 'text-red-500' };
    }
    if (strength === 2) {
      return { label: t('passwordStrength.fair', 'Fair'), color: 'bg-orange-400', textColor: 'text-orange-500' };
    }
    if (strength === 3) {
      return { label: t('passwordStrength.good', 'Good'), color: 'bg-yellow-400', textColor: 'text-yellow-600' };
    }
    return { label: t('passwordStrength.strong', 'Strong'), color: 'bg-green-500', textColor: 'text-green-600' };
  };

  const strengthInfo = getStrengthInfo();
  const allRequirementsMet = requirements.every((req) => req.met);

  return (
    <div className="space-y-3 mt-2">
      {/* Strength Bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
            {t('passwordStrength.label', 'Password Strength')}
          </span>
          <span className={cn('text-xs font-semibold', strengthInfo.textColor)}>
            {strengthInfo.label}
          </span>
        </div>
        <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full transition-all duration-300 rounded-full',
              strengthInfo.color
            )}
            style={{ width: `${((strength + 1) / 5) * 100}%` }}
          />
        </div>
      </div>

      {/* Requirements Checklist */}
      <div className="space-y-1.5">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
          {t('passwordStrength.requirements', 'Password Requirements')}
        </span>
        <div className="space-y-1">
          {requirements.map((requirement, index) => (
            <div
              key={index}
              className="flex items-center gap-2 text-xs"
            >
              {requirement.met ? (
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
              ) : (
                <X className="w-4 h-4 text-slate-400 flex-shrink-0" />
              )}
              <span
                className={cn(
                  'transition-colors',
                  requirement.met
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-slate-500 dark:text-slate-400'
                )}
              >
                {requirement.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Validation Summary */}
      {allRequirementsMet && (
        <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <Check className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
          <span className="text-xs font-medium text-green-700 dark:text-green-300">
            {t('passwordStrength.allMet', 'Password meets all requirements')}
          </span>
        </div>
      )}
    </div>
  );
}
